import { useMemo } from 'react';
import { getVal } from 'oolib';
import { getDerivedDimensionTitle } from '../WidgetBarChart/utils/_extractWidgetTitle';
import { getDerivedTimeSeriesTitle } from '../WidgetLineChart/utils';
import { getTempWidgetOption } from '../WidgetTemp_SHI/TEMP_WIDGETS';

/**
 * Unified hook for managing widget titles
 * Handles auto-generation and custom title tracking
 * 
 * @param {Object} params
 * @param {Object} params.widgetConfig - The widget configuration object
 * @param {string} params.widgetType - Type of widget (e.g., 'WidgetBarChart', 'WidgetLineChart')
 * @param {string} params.titlePath - Path to title in config (default: 'props.title')
 * @param {string} params.hasCustomTitlePath - Path to hasCustomTitle flag (default: 'props.hasCustomTitle')
 * @returns {Object} Title management utilities
 */
export const useWidgetTitle = ({
  widgetConfig,
  widgetType,
  titlePath = 'props.title',
  hasCustomTitlePath = 'props.hasCustomTitle'
}) => {
  
  const currentTitle = widgetConfig ? getVal(widgetConfig, titlePath) : undefined;
  const hasCustomTitle = widgetConfig ? (getVal(widgetConfig, hasCustomTitlePath) || false) : false;
  
  const generateDefaultTitle = useMemo(() => {
    return () => {
      switch (widgetType) {
        case 'WidgetBarChart':
        case 'WidgetPieChart':
        case 'WidgetHeatMapGrid': {
          const dim1 = getVal(widgetConfig, 'props.dimension1');
          const dim2 = getVal(widgetConfig, 'props.dimension2');
          return getDerivedDimensionTitle(dim1, dim2);
        }
        
        case 'WidgetLineChart': {
          const dateField = getVal(widgetConfig, 'props.dateField');
          const dimension = getVal(widgetConfig, 'props.dimension');
          const interval = getVal(widgetConfig, 'props.interval');
          return getDerivedTimeSeriesTitle({ dateField, dimension, interval });
        }

        case 'WidgetTemp_SHI': {
          // One Add Widget entry stands for many widgets, so the container's
          // own name is useless as a default — every card would read "Custom
          // Widget". Name it after what was actually picked.
          const tempWidget = getVal(widgetConfig, 'props.tempWidget');
          return getTempWidgetOption(tempWidget)?.display || null;
        }

        case 'WidgetMetricCard': {
          const selectedField = getVal(widgetConfig, 'props.field');
          if (!selectedField?.display) return null;
          const valueNames = getVal(widgetConfig, 'props.selectedFieldValueNames');
          if (Array.isArray(valueNames) && valueNames.length > 0) {
            return `${selectedField.display} - ${valueNames.join(', ')}`;
          }
          return selectedField.display;
        }

        default:
          return null;
      }
    };
  }, [widgetConfig, widgetType]);
  
  const displayTitle = useMemo(() => {
    if (currentTitle) {
      return currentTitle;
    }
    const defaultTitle = generateDefaultTitle();
    return defaultTitle || widgetConfig?.comp?.display || 'Untitled';
  }, [currentTitle, generateDefaultTitle, widgetConfig?.comp]);
  
  const shouldShowDefaultButton = useMemo(() => {
    if (!hasCustomTitle) return false;
    
    const defaultTitle = generateDefaultTitle();
    if (!defaultTitle) return false;
    
    return currentTitle !== defaultTitle;
  }, [hasCustomTitle, currentTitle, generateDefaultTitle]);
  
  return {
    currentTitle,
    displayTitle,
    hasCustomTitle,
    shouldShowDefaultButton,
    generateDefaultTitle,
  };
};


